// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import { isNonEmptyString } from "@insite/client-framework/Common/Utilities/StringUtilities";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { LanguageModel } from "@insite/client-framework/Types/ApiModels";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableCell, DataTableRow } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import ProductImage, { ProductImageStyles } from "@store/Overrides/Components/ProductImage";
import { formFieldLabelStyles } from "@store/Themes/common/mixins/formField";
import { priceStyles } from "@store/Themes/common/mixins/price";
import { productLineProps } from "@store/Themes/common/props/productLine";
import constants from "@store/constants";
import { InvoiceRmaLine, ReturnReasonModel } from "@store/Framework/Types/ApiModels";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

const returnReasonFieldsWrapperGap = 10;
const returnReasonTextFieldWidth = 55;

export interface RequestRmaProductListStyles {
    tableRow?: DataTableRowProps;
    tableCell?: DataTableCellBaseProps;
    productGridContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    infoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productInfoTextGridItem?: GridItemProps;
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
    mobileReturnReasonGridContainer?: GridContainerProps;
    mobileReturnReasonGridItem?: GridItemProps;
    invoiceRmaLineErrorInfosBox?: InfosBoxProps;
    qtyOrdered?: TypographyPresentationProps;
    fridgeRefTextField?: TextFieldPresentationProps;
    returnReasonFieldsWrapper?: InjectableCss;
    returnCellUpperSectionWrapper?: InjectableCss;
    qtyToReturnTextField?: TextFieldPresentationProps;
    valueWrapper?: InjectableCss;
    valueLabel?: TypographyPresentationProps;
    valueText?: TypographyPresentationProps;
    returnReasonSelect?: SelectPresentationProps;
    specifyIncorrectProductTextField?: TextFieldPresentationProps;
    nonReturnableMessageInfosBox?: InfosBoxProps;
    productInfoText?: TypographyPresentationProps;
}

const productDetailDescriptionStyles = css`
    width: 100%;
    ${wrapInContainerStyles}
    color: ${getColor("primary.dark")};
    font-weight: ${x => x.theme.design.typography.weight.base};
    line-height: ${x => x.theme.design.typography.lineHeight.small};
`;

const descriptionWrapperStyles = css`
    ${x => x.theme.formField.defaultProps?.cssOverrides?.descriptionWrapper}
    font-size: ${x => x.theme.design.typography.size.tiny};
    margin-top: 0 !important;
`;

const formInputWrapper = css`
    ${x => x.theme.formField.defaultProps?.cssOverrides?.formInputWrapper}
    margin-top: 0 !important;
`;

const labelProps: TypographyPresentationProps = {
    size: 12,
};

export const requestRmaProductListStyles: RequestRmaProductListStyles = {
    tableCell: {
        css: css`
            white-space: normal;
            vertical-align: top;
        `,
    },
    productGridContainer: productLineProps.contentContainer,
    imageGridItem: productLineProps.imageGridItem,
    productImage: productLineProps.image,
    infoGridItem: productLineProps.contentGridItem,
    productInfoGridContainer: productLineProps.infoContainer,
    productInfoDescriptionGridItem: productLineProps.descriptionGridItem,
    productInfoTextGridItem: productLineProps.partNumbersGridItem,
    productDetailLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: productDetailDescriptionStyles,
        },
    },
    productDetailDescription: {
        css: productDetailDescriptionStyles,
    },
    mobileReturnReasonGridContainer: {
        offsetCss: css`
            max-width: 100%;
            margin: ${pxToRem(15)} 0 0 0;
        `,
    },
    mobileReturnReasonGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    invoiceRmaLineErrorInfosBox: {
        type: "error",
        size: "small",
        extendedStyles: {
            wrapper: {
                css: css`
                    margin-top: ${pxToRem(10)};
                `,
            },
        },
    },
    fridgeRefTextField: {
        cssOverrides: {
            formField: css`
                ${x => x.theme.formField.defaultProps?.cssOverrides?.formField}
                min-width: ${pxToRem(150)};
                flex-basis: ${pxToRem(returnReasonTextFieldWidth)};
                ${breakpointValues(["100%", null, null, pxToRem(165), null], "max-width")}
                ${breakpointValues(pxToRemArray([5, null, null, 0, null]), "padding-top")}
                ${breakpointValues(pxToRemArray([1, null, null, 0, null]), "border-top-width")}
                border-top-color: ${getColor("common.border")};
                border-top-style: solid;
            `,
            formInputWrapper,
            descriptionWrapper: descriptionWrapperStyles,
        },
        labelProps,
    },
    returnReasonFieldsWrapper: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(returnReasonFieldsWrapperGap)};
            padding: ${pxToRem(8)};
            background-color: ${getColor("common.borderLight")};
            border-radius: 4px;
        `,
    },
    returnCellUpperSectionWrapper: {
        css: css`
            display: flex;
            flex: 1;
            gap: ${pxToRem(10)};
        `,
    },
    qtyToReturnTextField: {
        cssOverrides: {
            formField: css`
                ${x => x.theme.formField.defaultProps?.cssOverrides?.formField}
                flex-basis: ${pxToRem(returnReasonTextFieldWidth)};
                flex: 1;
            `,
            formInputWrapper,
            descriptionWrapper: descriptionWrapperStyles,
        },
        labelProps,
    },
    valueWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
        `,
    },
    valueLabel: {
        size: 12,
        css: css`
            ${formFieldLabelStyles()}
        `,
    },
    valueText: {
        size: 18,
        css: css`
            ${priceStyles()}
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    returnReasonSelect: {
        cssOverrides: {
            formField: css`
                ${x => x.theme.formField.defaultProps?.cssOverrides?.formField}
                flex-basis: calc(
                    100% - (${pxToRem(returnReasonTextFieldWidth)} + ${pxToRem(returnReasonFieldsWrapperGap)})
                );
            `,
            formInputWrapper,
            descriptionWrapper: descriptionWrapperStyles,
        },
        labelProps,
    },
    specifyIncorrectProductTextField: {
        cssOverrides: {
            formField: css`
                ${x => x.theme.formField.defaultProps?.cssOverrides?.formField}
                flex-basis: calc(
                    100% - (${pxToRem(returnReasonTextFieldWidth)} + ${pxToRem(returnReasonFieldsWrapperGap)})
                );
            `,
            formInputWrapper,
            descriptionWrapper: descriptionWrapperStyles,
        },
        labelProps,
    },
    nonReturnableMessageInfosBox: {
        type: "warning",
        size: "small",
    },
    productInfoText: {
        size: 14,
    },
};

const styles = requestRmaProductListStyles;

type ReturnRequestLineCardProps = {
    invoiceRmaLine: InvoiceRmaLine;
    returnReasons: ReturnReasonModel[];
    totalQuantity: number;
    invoiceRmaLineError?: string | null;
    language?: LanguageModel;
    currencySymbol?: string;
    updateInvoiceRmaLineChangeHandler: (
        invoiceRmaLine: InvoiceRmaLine,
        updatedProps: SafeDictionary<string | number | boolean>,
    ) => void;
    returnReasonChangeHandler: (event: React.ChangeEvent<HTMLSelectElement>, invoiceRmaLine: InvoiceRmaLine) => void;
};

const ReturnRequestLineCard = ({
    invoiceRmaLine,
    returnReasons,
    totalQuantity,
    invoiceRmaLineError,
    language,
    currencySymbol,
    updateInvoiceRmaLineChangeHandler,
    returnReasonChangeHandler,
}: ReturnRequestLineCardProps) => {
    const [localRmaQtyRequested, setLocalRmaQtyRequested] = useState(invoiceRmaLine.rmaQtyRequested.toString() || "");
    const [localFridgeReference, setLocalFridgeReference] = useState(invoiceRmaLine.fridgeReference || "");
    const [localSpecifyIncorrectProduct, setLocalSpecifyIncorrectProduct] = useState(
        invoiceRmaLine.returnReasonComments || "",
    );
    const uid = useMemo(() => uniqueId(), []);
    const isDesktop = useIsBreakpoint("lg", "up");
    const isTablet = useIsBreakpoint("sm", "up");

    useEffect(() => {
        return () => {
            setLocalRmaQtyRequested("");
            setLocalFridgeReference("");
            setLocalSpecifyIncorrectProduct("");
        };
    }, []);

    const qtyOrdered = useMemo(() => {
        return invoiceRmaLine.line.properties.quantity
            ? parseInt(invoiceRmaLine.line.properties.quantity, 10)
            : invoiceRmaLine.line.qtyInvoiced;
    }, [invoiceRmaLine.line]);

    const qtyToReturnError = useMemo(() => {
        const requestedQty = parseInt(localRmaQtyRequested, 10);

        if (Number.isNaN(requestedQty)) {
            return false;
        }

        return (
            (totalQuantity > 0 &&
                requestedQty >
                    (invoiceRmaLine.line.properties.quantity
                        ? parseInt(invoiceRmaLine.line.properties.quantity, 10)
                        : invoiceRmaLine.line.qtyInvoiced) &&
                siteMessage(
                    "Field_Max_Number",
                    translate("QTY Returning"),
                    (invoiceRmaLine.line.properties.quantity
                        ? parseInt(invoiceRmaLine.line.properties.quantity, 10)
                        : invoiceRmaLine.line.qtyInvoiced
                    ).toString(),
                )) ||
            (requestedQty < 0 && siteMessage("Field_Min_Number", translate("QTY Returning"), "0"))
        );
    }, [totalQuantity, localRmaQtyRequested, invoiceRmaLine.line]);

    const fridgeRefError = useMemo(() => {
        if (parseInt(localRmaQtyRequested) > 0 && invoiceRmaLine.isFridgeProduct) {
            if (!localFridgeReference) {
                return siteMessage("Field_Required", translate("Fridge ref"));
            }
            if (localFridgeReference.length < 8) {
                return translate("Fridge ref must contain 8 characters");
            }
        }
        return false;
    }, [localRmaQtyRequested, localFridgeReference, invoiceRmaLine.isFridgeProduct]);

    const returnReasonSelectError = useMemo(() => {
        return (
            parseInt(localRmaQtyRequested) > 0 &&
            !invoiceRmaLine.rmaReasonCode &&
            siteMessage("Field_Required", translate("Return Reason"))
        );
    }, [localRmaQtyRequested, invoiceRmaLine.rmaReasonCode]);

    const specifyIncorrectProductError = useMemo(() => {
        return (
            invoiceRmaLine.rmaReasonCode === constants.rmaReasonCode.incorrectProductReceived &&
            !localSpecifyIncorrectProduct &&
            siteMessage("Field_Required", translate("Specify incorrect product"))
        );
    }, [localSpecifyIncorrectProduct, invoiceRmaLine.rmaReasonCode]);

    const debouncedUpdateInvoiceRmaLineChangeHandler = useCallback(
        debounce((updatedProps: SafeDictionary<string | number | boolean>) => {
            const hasAnyLocalLineFieldError = !!(qtyToReturnError || fridgeRefError);
            const returnReasonFieldError = !!(returnReasonSelectError || specifyIncorrectProductError);

            updateInvoiceRmaLineChangeHandler(invoiceRmaLine, {
                ...updatedProps,
                hasAnyLocalLineFieldError,
                returnReasonFieldError,
            });
        }, 200),
        [
            qtyToReturnError,
            fridgeRefError,
            returnReasonSelectError,
            specifyIncorrectProductError,
            updateInvoiceRmaLineChangeHandler,
            invoiceRmaLine,
        ],
    );

    useEffect(() => {
        if (parseInt(localRmaQtyRequested) !== invoiceRmaLine.rmaQtyRequested) {
            debouncedUpdateInvoiceRmaLineChangeHandler({
                rmaQtyRequested: localRmaQtyRequested ? parseInt(localRmaQtyRequested) || 0 : 0,
            });
        }
    }, [localRmaQtyRequested]);

    useEffect(() => {
        if (localFridgeReference !== invoiceRmaLine.fridgeReference) {
            debouncedUpdateInvoiceRmaLineChangeHandler({ fridgeReference: localFridgeReference });
        }
    }, [localFridgeReference]);

    useEffect(() => {
        if (localSpecifyIncorrectProduct !== invoiceRmaLine.returnReasonComments) {
            debouncedUpdateInvoiceRmaLineChangeHandler({ returnReasonComments: localSpecifyIncorrectProduct });
        }
    }, [localSpecifyIncorrectProduct]);

    const returnValue = useMemo(() => {
        const amount =
            qtyToReturnError || !isNonEmptyString(localRmaQtyRequested)
                ? 0
                : invoiceRmaLine.line.unitPrice * parseInt(localRmaQtyRequested);

        return language && currencySymbol
            ? getLocalizedCurrency({
                  amount,
                  language,
                  currencySymbol,
              })
            : amount;
    }, [language, currencySymbol, invoiceRmaLine.line.unitPrice, localRmaQtyRequested, qtyToReturnError]);

    const lineEditable = useMemo(() => {
        return !invoiceRmaLine.nonReturnableMessage && qtyOrdered > 0;
    }, [invoiceRmaLine.nonReturnableMessage, qtyOrdered]);

    const description = useMemo(() => {
        return invoiceRmaLine.line.shortDescription || invoiceRmaLine.line.description;
    }, [invoiceRmaLine.line]);

    const fridgeRefFormElement = (
        <TextField
            {...styles.fridgeRefTextField}
            id={`fridgeRefTextField-${uid}`}
            label={!isDesktop ? translate("Fridge ref") : ""}
            type="text"
            value={localFridgeReference || ""}
            minLength={8}
            maxLength={8}
            disabled={!invoiceRmaLine.isFridgeProduct}
            required={parseInt(localRmaQtyRequested) > 0 && invoiceRmaLine.isFridgeProduct}
            error={fridgeRefError}
            data-test-selector={`returnRequest_fridgeRef_${invoiceRmaLine.line.id}`}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setLocalFridgeReference(event.target.value)}
        />
    );

    const returnReasonFieldElements = (
        <StyledWrapper {...styles.returnReasonFieldsWrapper}>
            <StyledWrapper {...styles.returnCellUpperSectionWrapper}>
                <TextField
                    {...styles.qtyToReturnTextField}
                    id={`qtyToReturnTextField-${uid}`}
                    label={translate("Qty")}
                    type="number"
                    value={localRmaQtyRequested}
                    error={qtyToReturnError}
                    data-test-selector={`returnRequest_qtyToReturn_${invoiceRmaLine.line.id}`}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setLocalRmaQtyRequested(event.target.value)
                    }
                />
                <StyledWrapper {...styles.valueWrapper}>
                    <Typography {...styles.valueLabel}>{translate("Value")}</Typography>
                    <Typography {...styles.valueText}>{returnValue}</Typography>
                </StyledWrapper>
            </StyledWrapper>
            {!!returnReasons.length && (
                <>
                    <Select
                        {...styles.returnReasonSelect}
                        inputId={`returnReasonSelect-${uid}`}
                        label={translate("Return Reason")}
                        value={invoiceRmaLine.rmaReasonCode || ""}
                        required={parseInt(localRmaQtyRequested) > 0}
                        error={returnReasonSelectError}
                        data-test-selector={`returnRequest_ReturnReason_${invoiceRmaLine.line.id}`}
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                            returnReasonChangeHandler(event, invoiceRmaLine)
                        }
                    >
                        <option value="">{translate("Select a Reason Code")}</option>
                        {returnReasons?.map(({ code, description }) => (
                            <option key={code} value={code}>
                                {description}
                            </option>
                        ))}
                    </Select>
                    {invoiceRmaLine.rmaReasonCode === constants.rmaReasonCode.incorrectProductReceived && (
                        <TextField
                            {...styles.specifyIncorrectProductTextField}
                            id={`specifyIncorrectProductTextField-${uid}`}
                            label={translate("Specify incorrect product")}
                            type="text"
                            value={localSpecifyIncorrectProduct}
                            required={invoiceRmaLine.rmaReasonCode === constants.rmaReasonCode.incorrectProductReceived}
                            error={specifyIncorrectProductError}
                            data-test-selector={`returnRequest_specifyIncorrectProduct_${invoiceRmaLine.line.id}`}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                setLocalSpecifyIncorrectProduct(event.target.value)
                            }
                        />
                    )}
                </>
            )}
        </StyledWrapper>
    );

    const nonReturnableMessageInfosBoxElement = (
        <InfosBox {...styles.nonReturnableMessageInfosBox}>{invoiceRmaLine.nonReturnableMessage}</InfosBox>
    );

    return (
        <DataTableRow {...styles.tableRow}>
            <DataTableCell {...styles.tableCell} className="description-column">
                <GridContainer {...styles.productGridContainer}>
                    <GridItem {...styles.imageGridItem}>
                        <ProductImage
                            product={{ ...invoiceRmaLine.line } as any}
                            size="medium"
                            extendedStyles={styles.productImage}
                            href={invoiceRmaLine.line.productUri}
                        />
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <GridContainer {...styles.productInfoGridContainer}>
                            {description && (
                                <GridItem {...styles.productInfoDescriptionGridItem}>
                                    {invoiceRmaLine.line.productUri ? (
                                        <Link
                                            {...styles.productDetailLink}
                                            href={invoiceRmaLine.line.productUri}
                                            alt={`See product details for ${description}`}
                                        >
                                            {description}
                                        </Link>
                                    ) : (
                                        <Typography {...styles.productDetailDescription}>{description}</Typography>
                                    )}
                                </GridItem>
                            )}
                            {invoiceRmaLine.line.productERPNumber && (
                                <GridItem {...styles.productInfoTextGridItem}>
                                    <ProductPartNumbers
                                        productNumber={invoiceRmaLine.line.productERPNumber.substring(10)}
                                    />
                                </GridItem>
                            )}
                            {invoiceRmaLine.line.properties &&
                                "batchNumber" in invoiceRmaLine.line.properties &&
                                invoiceRmaLine.line.properties.batchNumber && (
                                    <GridItem {...styles.productInfoTextGridItem}>
                                        <Typography {...styles.productInfoText}>
                                            <b>{translate("Batch no :")}</b>&nbsp;
                                            {invoiceRmaLine.line.properties.batchNumber}
                                        </Typography>
                                    </GridItem>
                                )}
                            {invoiceRmaLine.line.properties &&
                                "expiryOn" in invoiceRmaLine.line.properties &&
                                invoiceRmaLine.line.properties.expiryOn && (
                                    <GridItem {...styles.productInfoTextGridItem}>
                                        <Typography {...styles.productInfoText}>
                                            <b>{translate("Expiry on :")}</b>&nbsp;
                                            {invoiceRmaLine.line.properties.expiryOn}
                                        </Typography>
                                    </GridItem>
                                )}
                            {!isDesktop && (
                                <>
                                    <GridItem {...styles.productInfoTextGridItem}>
                                        <Typography {...styles.productInfoText}>
                                            <b>{translate("Quantity :")}</b>&nbsp;
                                            {qtyOrdered}
                                        </Typography>
                                    </GridItem>
                                    {lineEditable && (
                                        <GridItem {...styles.productInfoTextGridItem}>{fridgeRefFormElement}</GridItem>
                                    )}
                                </>
                            )}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
                {!isDesktop && !isTablet && (
                    <GridContainer {...styles.mobileReturnReasonGridContainer}>
                        <GridItem {...styles.mobileReturnReasonGridItem}>
                            {lineEditable ? returnReasonFieldElements : nonReturnableMessageInfosBoxElement}
                        </GridItem>
                    </GridContainer>
                )}
                {invoiceRmaLineError && (
                    <InfosBox {...styles.invoiceRmaLineErrorInfosBox}>{invoiceRmaLineError}</InfosBox>
                )}
            </DataTableCell>
            {isDesktop && (
                <DataTableCell {...styles.tableCell} className="quantity-column">
                    <Typography {...styles.qtyOrdered}>{qtyOrdered}</Typography>
                </DataTableCell>
            )}
            {lineEditable && (isDesktop || isTablet) ? (
                <>
                    {isDesktop && (
                        <DataTableCell {...styles.tableCell} className="fridgeref-column">
                            {fridgeRefFormElement}
                        </DataTableCell>
                    )}
                    {isTablet && (
                        <DataTableCell {...styles.tableCell} className="return-column">
                            {returnReasonFieldElements}
                        </DataTableCell>
                    )}
                </>
            ) : (
                (isDesktop || isTablet) && (
                    <DataTableCell {...styles.tableCell} colSpan={isDesktop ? 2 : 1} className="return-column">
                        {nonReturnableMessageInfosBoxElement}
                    </DataTableCell>
                )
            )}
        </DataTableRow>
    );
};

export default ReturnRequestLineCard;
